import { AnalyticsContext } from "@jidlomat/terminal-core/contexts/analyticsPort";
import { MixpanelTabletTypeEnum } from "@jidlomat/terminal-core/types/mixpanelTypes";
import { setAnalyticsInstance } from "@jidlomat/terminal-core/utils/analytics/analyticsUtils";
import { getFridgeId } from "@jidlomat/terminal-core/utils/browser/localStorage";
import { log, logToSentry } from "@jidlomat/terminal-core/utils/logger";
import { shouldEnableFeature } from "@jidlomat/terminal-core/utils/shouldEnableFeature";
import mixpanel, { type Dict, type Response as MixpanelTrackResponse } from "mixpanel-browser";
import { type FC, type PropsWithChildren, useCallback, useEffect, useMemo, useRef } from "react";
import { v4 as uuid, v5 as uuidv5 } from "uuid";
import type { FlowKind } from "../flow/loadFlow";
import { Config } from "../services/configuration/eatSmartConfig";
import { sendCloudwatchMetric } from "../services/external/cloudwatch";
import {
	identifyCustomerIo,
	loadCustomerIoOnce,
	resetCustomerIo,
	setCustomerIoAnonymousId,
	trackCustomerIo,
} from "../utils/analytics/customerIoClient";
import { confirmEventSent, releaseEvent, tryReserveEvent } from "../utils/analytics/sentEventsRegistry";

const CLOUDWATCH_UPTIME_CHECK_INTERVAL = 30_000;
// Fixed namespace for UUID v5 generation of Mixpanel $insert_id (RFC 4122 app-specific namespace)
const MIXPANEL_DEDUP_NAMESPACE = "a11e4d6c-b8f0-4a3e-9c7d-2f5b8e1a3d09";

const MIXPANEL_INIT_OPTIONS = {
	api_host: "https://api-eu.mixpanel.com",
	property_blacklist: [
		"$city",
		"$region",
		"mp_country_code",
		"$browser",
		"$browser_version",
		"$device",
		"$current_url",
		"$initial_referrer",
		"$initial_referring_domain",
		"$os",
		"mp_lib",
		"$referrer",
		"$referring_domain",
		"$screen_height",
		"$screen_width",
		"$search_engine",
		"mp_keyword",
	],
};

// Generate deterministic insert_id (valid UUID) for event deduplication
// Uses UUID v5 to create a deterministic UUID from event name + identifier,
// ensuring different event types for the same entity get distinct IDs
const mixpanelInsertId = (name: string, props: Dict, sessionId: string): string => {
	const eventIdentifier = props.$order_id || props.$user_id || props.$dish_type_id || "";
	const dedupKey = eventIdentifier ? `${name}:${eventIdentifier}` : `${name}:${sessionId}`;
	return uuidv5(dedupKey, MIXPANEL_DEDUP_NAMESPACE);
};

/** Hands the event to Mixpanel; the reservation of an order event is settled by the SDK's answer. */
const trackMixpanel = (name: string, props: Dict, insertId: string, reservationToken: string | undefined) => {
	// The SDK reports acceptance through this callback: under batching (the
	// default) it fires with 1 once the event sits in the SDK's durable
	// localStorage queue, which owns retries from there; without batching
	// (no localStorage) it carries the actual send outcome. It confirms the
	// claim (48h TTL) or releases it so a failed hand-off stays retryable;
	// both are gated on the owning token, so a late callback cannot settle a
	// claim another instance retried after the pending TTL.
	const settleReservation = reservationToken
		? (response: MixpanelTrackResponse) => {
				if (response === 1) {
					confirmEventSent(insertId, reservationToken);
				} else {
					releaseEvent(insertId, reservationToken);
				}
			}
		: undefined;

	try {
		mixpanel.track(
			name,
			{
				...props,
				$insert_id: insertId,
			},
			settleReservation,
		);
	} catch (trackError) {
		// The event never reached the SDK — free the id for an immediate retry
		// instead of letting the reservation run out its TTL.
		if (reservationToken) {
			releaseEvent(insertId, reservationToken);
		}
		throw trackError;
	}
};

type AnalyticsContextProviderProps = PropsWithChildren<{
	/** The flow of the fridge, sent with every event as $tablet_flow. */
	flow?: FlowKind;
}>;

/**
 * AnalyticsContextProvider component
 */
export const AnalyticsContextProvider: FC<AnalyticsContextProviderProps> = ({ children, flow = "rfid" }) => {
	const mixpanelInstance = mixpanel;
	// The Customer.io instance lives in module scope (utils/analytics/customerIoClient), not in
	// a ref: a per-mount ref rebuilt the SDK — and re-loaded it — on every provider remount.
	const fridgeId = getFridgeId();
	const initializedRef = useRef(false);
	const sessionIdRef = useRef<string>(uuid());
	const hasReportedSuppressionRef = useRef(false);
	const AppVersion = import.meta.env.APP_VERSION;
	const AppBuild = import.meta.env.VITE_BUILD_NUMBER;

	const track = useCallback(
		(name: string, props: Dict) => {
			try {
				const useAnalytics = shouldEnableFeature("useAnalytics");
				const insertId = mixpanelInsertId(name, props, sessionIdRef.current);

				// Order-scoped events are at-most-once per order, ever. Remounts, reloads and
				// ghost webview instances re-fire them, and because reset() rotates the
				// distinct_id after every order, Mixpanel's server-side $insert_id dedup misses
				// the late re-fires — so they must be suppressed here (JID-2347). Events keyed
				// by user/dish/session skip the registry: the $insert_id has no occurrence
				// component, so their same-identity repeats are already collapsed server-side,
				// and a 48h registry hold would also swallow the legitimate cross-session ones.
				const isOrderScopedEvent = Boolean(props.$order_id);

				if (!useAnalytics) {
					log("Not master tablet, not tracking", "info", false, name, {
						...props,
						insertId: insertId,
					});
					return;
				}

				// Check and claim in one registry call: localStorage has no compare-and-swap,
				// so the claim is verified by an ownership nonce read back inside — null means
				// the id was already sent, or another instance owns the in-flight claim.
				const reservationToken = isOrderScopedEvent ? tryReserveEvent(insertId) : undefined;
				if (reservationToken === null) {
					// Plain args only — this line becomes a Sentry breadcrumb on every skip.
					log("Skipping already-sent order event", "info", name, props.$order_id);
					if (!hasReportedSuppressionRef.current) {
						// One countable Sentry signal per app instance — enough to see which
						// kiosks/realms re-fire order events, without per-skip event spam.
						hasReportedSuppressionRef.current = true;
						logToSentry("Suppressed re-track of an already-sent order event", "info");
					}
					return;
				}

				trackMixpanel(name, props, insertId, reservationToken);

				trackCustomerIo(
					name,
					{
						...props,
						// Mirrors Mixpanel's registered $session_id super property, which Customer.io
						// has no equivalent of — it has to ride along on every event instead.
						$session_id: sessionIdRef.current,
						$source_of_stats: "Tablet",
						$tabletType: MixpanelTabletTypeEnum.Master,
						$fridge_id: fridgeId,
						$tablet_version: AppVersion,
						$tablet_build: AppBuild,
						$tablet_flow: flow,
					},
					// The same deterministic id Mixpanel dedups on, so Customer.io drops re-sends of
					// an order event too. Customer.io ignores a repeated messageId for 12h from
					// receipt, and the insert ids of non-order events have no occurrence component
					// (name:userId / name:dishTypeId / name:sessionId), so reusing them there would
					// swallow legitimate repeats — order-scoped events only (JID-2139).
					isOrderScopedEvent ? { messageId: insertId } : undefined,
				);
			} catch (e) {
				// Analytics is best-effort: never let a tracking failure surface to the caller.
				log("Error while tracking event", "error", e);
			}
		},
		[fridgeId, flow],
	);

	/**
	 * Customer.io's anonymous identity for the current session. The anonymous id has to be the
	 * session id *before* the anonymous identify, or Customer.io mints an orphan anonymous
	 * profile that the session's later events never join. Used at start-up and after reset(),
	 * which both begin a session that needs its own anonymous identity.
	 *
	 * Two adapter calls rather than one combined verb: each swallows its own failure, so a
	 * failing setAnonymousId must not skip the identify. `sessionIdRef.current` is read at call
	 * time, which is what makes the post-reset call carry the freshly rotated id.
	 *
	 * Gated on the master tablet like track(): an anonymous identify is still an identify call,
	 * and a non-master tablet must send nothing to either SDK (S06-AC2).
	 */
	const identifyAnonymously = useCallback((): void => {
		if (!shouldEnableFeature("useAnalytics")) {
			return;
		}

		setCustomerIoAnonymousId(sessionIdRef.current);
		identifyCustomerIo(undefined, {
			device_id: fridgeId,
			session_id: sessionIdRef.current,
		});
	}, [fridgeId]);

	const reset = useCallback(() => {
		try {
			// Generate new session ID on reset
			const newSessionId = uuid();
			sessionIdRef.current = newSessionId;

			const commonProperties = {
				$session_id: newSessionId,
				$source_of_stats: "Tablet",
				$tabletType: MixpanelTabletTypeEnum.Master,
				$fridge_id: fridgeId,
				$tablet_version: AppVersion,
				$tablet_build: AppBuild,
				$tablet_flow: flow,
			};

			// Reset Mixpanel
			mixpanelInstance.reset();
			mixpanelInstance.register(commonProperties);

			// Reset Customer.io (the write-key gate lives in the adapter), then give the new
			// session the same anonymous identity treatment as start-up: without it the next
			// order's anonymous activity either lands on the previous session's identity or on
			// an orphan profile of the SDK's own making. Gated like the load: a display tablet
			// never loads the SDK, so a reset handed to it would sit in the pre-init buffer.
			if (shouldEnableFeature("useAnalytics")) {
				resetCustomerIo();
			}
			identifyAnonymously();
		} catch (e) {
			log("Error while resetting analytics", "error", e);
		}
	}, [fridgeId, flow, identifyAnonymously]);

	const identify = useCallback(
		(userId: string, userData?: { [key: string]: string | number | null }) => {
			try {
				// Same gate as track(): on a non-master tablet no event and no identify call may
				// reach either SDK (S06-AC2). Mixpanel's identify() is a network call of its own
				// ($identify), and it is also what flushes the queued people.set profile update —
				// leaving it ungated made a passive display send both.
				if (!shouldEnableFeature("useAnalytics")) {
					log("Not master tablet, not identifying", "info", userId);

					return;
				}

				// Identify user in Mixpanel
				mixpanelInstance.identify(userId);

				// Identify user in Customer.io with anonymous ID (sessionId)
				// This allows CustomerIO to associate previous anonymous events
				// from this session with the identified user
				const traits = {
					...userData,
					// Keep fridgeId as device_id for device tracking
					device_id: fridgeId,
				};
				identifyCustomerIo(userId, traits);
			} catch (e) {
				log("Error while identifying user", "error", e);
			}
		},
		[fridgeId],
	);

	const people = useMemo(
		() => ({
			set: (data: { [key: string]: string | number }) => {
				try {
					// Set user properties in Mixpanel
					mixpanelInstance.people.set(data);
				} catch (e) {
					log("Error while setting people properties", "error", e);
				}
			},
		}),
		[],
	);

	useEffect(() => {
		if (initializedRef.current) {
			return; // guard clause
		}
		initializedRef.current = true;

		const commonProperties = {
			$session_id: sessionIdRef.current,
			$source_of_stats: "Tablet",
			$tabletType: MixpanelTabletTypeEnum.Master,
			$fridge_id: fridgeId,
			$tablet_version: AppVersion,
			$tablet_build: AppBuild,
			$tablet_flow: flow,
		};

		// Initialize Mixpanel. Kept inside the effect (not the render body) so a synchronous
		// failure can never escape into render, where the root boundary would catch it and reload the page.
		try {
			mixpanelInstance.init(Config.tools.mixpanelProjectId, MIXPANEL_INIT_OPTIONS);
			mixpanelInstance.register(commonProperties);
		} catch (e) {
			log("Error while initializing Mixpanel", "error", e);
		}

		// Initialize Customer.io. Every call is gated on the write key and swallows its own
		// failures inside the adapter; the load happens at most once per page realm, however
		// often this provider starts up, and only on the master tablet — the load itself is a
		// request to cdp-eu.customer.io, which a display tablet must not make (S06-AC2). The
		// anonymous identify is once per start-up instead: the initializedRef guard above keeps
		// StrictMode's double-invocation to one call (refs survive its simulated remount), while
		// a genuine remount mints a new session id and legitimately deserves its own anonymous
		// identity, exactly like a reset.
		if (shouldEnableFeature("useAnalytics")) {
			loadCustomerIoOnce();
		}
		identifyAnonymously();

		setAnalyticsInstance({
			track: track,
			reset: reset,
			identify: identify,
			people: people,
		});
	}, [track, reset, identify, people, fridgeId, flow, identifyAnonymously]);

	useEffect(() => {
		const shouldTrackToCloudwatch = shouldEnableFeature("useCloudwatch");
		const interval = setInterval(() => {
			sendCloudwatchMetric(shouldTrackToCloudwatch);
		}, CLOUDWATCH_UPTIME_CHECK_INTERVAL);

		return () => {
			clearInterval(interval);
		};
	}, []);

	const contextValue = useMemo(
		() => ({
			track: track,
			reset: reset,
			identify: identify,
			people: people,
		}),
		[track, reset, identify, people],
	);

	return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
};
